/**
 * MSAL configuration for Microsoft Entra ID SSO (SPA / PKCE flow).
 *
 * Values come from Vite env vars:
 *   VITE_AZURE_AD_CLIENT_ID    - Application (client) ID of the Entra SPA app
 *   VITE_AZURE_AD_TENANT_ID    - Tenant ID (or 'common' for multi-tenant)
 *   VITE_AZURE_AD_REDIRECT_URI - Defaults to window.location.origin
 */
import { LogLevel, type Configuration, type RedirectRequest } from '@azure/msal-browser'

const clientId = import.meta.env.VITE_AZURE_AD_CLIENT_ID ?? ''
const tenantId = import.meta.env.VITE_AZURE_AD_TENANT_ID ?? 'common'
const redirectUri =
  import.meta.env.VITE_AZURE_AD_REDIRECT_URI ??
  (typeof window !== 'undefined' ? window.location.origin : '')

export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri,
    postLogoutRedirectUri: redirectUri,
  },
  cache: {
    cacheLocation: 'localStorage', // persist across tabs/reloads → avoids re-MFA
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return
        if (level === LogLevel.Error) console.error(message)
      },
      logLevel: LogLevel.Error,
    },
  },
}

/** Scopes requested at interactive sign-in. */
export const loginRequest: RedirectRequest = {
  scopes: ['User.Read', 'openid', 'profile', 'email'],
}

/**
 * Scopes requested when acquiring a token for our own API.
 * Using `<clientId>/.default` yields an access token whose `aud` is this app,
 * which the Django backend validates via JWKS.
 */
export const apiTokenRequest = {
  scopes: clientId ? [`${clientId}/.default`] : ['User.Read'],
}

export const msalConfigured = Boolean(clientId)
